import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DivisionContextService } from '@app/core/services/division/division-context.service';
import { FormControlService } from '@app/core/services/form/form-control.service';
import { SessionService } from '@app/core/services/session/session.service';
import { Division } from '@app/features/division/models/division.interface';
import { Section, SectionValue } from '@app/features/division/models/section.interface';
import { DivisionService } from '@app/features/division/services/division.service';
import { SectionService } from '@app/features/division/services/section.service';
import { FieldOption, FormField } from '@app/shared/models/field.interface';
import { combineLatest, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

export interface SectionValueField {
  field: FormField;
  options: FieldOption[];
}
@Component({
  selector: 'vfs-division-dropdown',
  templateUrl: './division-dropdown.component.html',
  styleUrls: ['./division-dropdown.component.scss']
})
export class DivisionDropdownComponent implements OnInit {
  formGroup: FormGroup;
  initialState = {};
  sectionValueFields: SectionValueField[] = [];
  all$: Observable<any>;
  divisions: Division[] = [];
  sections: Section[] = [];

  @Output('closeDivisionDropdown') closeDropdown = new EventEmitter();

  constructor(
    protected sessionService: SessionService,
    protected sectionService: SectionService,
    protected divisionService: DivisionService,
    protected divisionContextService: DivisionContextService,
    protected formControlService: FormControlService,
    protected formBuilder: FormBuilder
  ) {
    this.formGroup = this.formBuilder.group({});
  }

  ngOnInit() {
    this.all$ = combineLatest(this.sectionService.getAll(), this.divisionService.getOfLoggedUser()).pipe(
      tap(result => {
        this.sections = result[0];
        this.divisions = result[1];
        this.buildForm();
      })
    );
  }

  buildForm() {
    this.sections.forEach(section => {
      this.initialState[section.id] = [];
      const field: FormField = {
        label: section.name,
        formControlName: section.id,
        required: false
      };
      this.sectionValueFields.push({
        field,
        options: this.getSortedOptions(section)
      });
      this.formControlService.addToFormGroup(this.formGroup, section.id, []);
    });
    this.formGroup.patchValue(this.divisionContextService.divisionFilter$.getValue().filter);
  }

  getSortedOptions(section: Section): FieldOption[] {
    return section.values
      .filter(sv => sv.active && this.isVisible(sv))
      .map(sv2 => ({
        label: sv2.description,
        value: sv2.id
      }))
      .sort((a, b) => (a.label > b.label ? 1 : -1));
  }

  isVisible(sv: SectionValue): boolean {
    return this.divisions.some(div =>
      div.divisionSectionValues.some(sv2 => (sv2.sectionValue as SectionValue).id === sv.id)
    );
  }

  onClear() {
    this.formGroup.reset(this.initialState);
  }

  onCancel() {
    this.closeDropdown.emit();
  }

  checkMatchingDivisions(): string[] {
    // 1. Get all possible divisions that match the criteria
    const selectedSv: string[][] = Object.values(this.formGroup.value);
    for (let i = 0; i < selectedSv.length; ++i) {
      if (selectedSv[i].length === 0) {
        selectedSv[i] = this.sections[i].values.map(v => v.id);
      }
    }
    let divisions: string[][] = [];
    let division = [];
    this.setDivision(selectedSv, 0, divisions, division);
    // 2. Remove the ones I don't see
    const divisionIds = [];
    divisions.forEach(candidate => {
      let divisionFound = this.divisions.find(div =>
        div.divisionSectionValues
          .map(dsv => (dsv.sectionValue as SectionValue).id)
          .every(element => candidate.includes(element))
      );
      if (divisionFound && !divisionIds.includes(divisionFound.id)) {
        divisionIds.push(divisionFound.id);
      }
    });
    return divisionIds;
  }

  onAccept() {
    const divisionIds = this.checkMatchingDivisions();
    if (divisionIds.length !== 0) {
      this.divisionContextService.setDivisionFilter({
        filter: this.formGroup.value,
        divisions: divisionIds
      });
    }
    this.closeDropdown.emit();
  }

  protected setDivision(valuesForAssign: string[][], level: number, divisions: string[][], division: string[]) {
    if (level === valuesForAssign.length - 1) {
      valuesForAssign[level].forEach(value => {
        division.push(value);
        let divisionToPush = division.slice(0);
        divisions.push(divisionToPush);
        division.pop();
      });
    } else {
      valuesForAssign[level].forEach(value => {
        division.push(value);
        this.setDivision(valuesForAssign, level + 1, divisions, division);
        division.pop();
      });
    }
  }

  onSectionValueChange(ids: string[], sectionValueField: SectionValueField, sectionValueFields: SectionValueField[]) {
    const divisions: Division[] = [];
    this.checkMatchingDivisions().forEach(div => {
      divisions.push(this.divisions.find(dv => dv.id === div));
    });
    sectionValueFields.forEach(svf => {
      const filterNeeded = svf.field.formControlName !== sectionValueField.field.formControlName;
      const availableSv = [
        ...new Set(
          divisions
            .map(div => div.divisionSectionValues.map(dsv => (dsv.sectionValue as SectionValue).id))
            .reduce((acc, current) => {
              acc.push(...current);
              return acc;
            }, [])
        )
      ];
      svf.options = this.getSortedOptions(this.sections.find(sec => sec.id === svf.field.formControlName));
      if (filterNeeded) {
        svf.options = svf.options.filter(opt => availableSv.includes(opt.value));
      }
    });
  }
}
